import type { EditState, NodeId, Vec2 } from 'web';
import type { Edge, Node } from '@/types/node';

type NodeUndoEntry =
  | { type: 'add'; nodeId: NodeId }
  | { type: 'remove'; node: Node }
  | { type: 'update'; node: Node }
  | { type: 'position'; nodeId: NodeId; position: Vec2 };
type EdgeUndoEntry = { type: 'add'; edgeId: string } | { type: 'remove'; edge: Edge };

type Change =
  | { type: 'edge'; entry: EdgeUndoEntry }
  | { type: 'node'; entry: NodeUndoEntry }
  | { type: 'wasm'; entry: EditState[] }
  // A side-effecting undo step that isn't a graph mutation (e.g. file rename, which
  // lives in React/parent state, not the WASM graph). `run` performs the step and
  // returns the inverse entry, mirroring how node 'update' restores-and-returns.
  | { type: 'callback'; run: () => UndoRedoEntry };
export class UndoRedoEntry {
  changes?: Change[];

  private constructor(changes?: Change[]) {
    this.changes = changes;
  }

  static empty(): UndoRedoEntry {
    return new UndoRedoEntry();
  }

  static fromEdges(entries: EdgeUndoEntry[]) {
    return new UndoRedoEntry(entries.map((entry) => ({ type: 'edge', entry })));
  }

  static fromNodes(entries: NodeUndoEntry[]) {
    return new UndoRedoEntry(entries.map((entry) => ({ type: 'node', entry })));
  }

  /**
   * A call that applied no edits has nothing to reverse, so it must not leave an
   * entry behind: undoing it would look like a dropped keystroke.
   */
  static fromWasmEdit(undoState?: EditState[]) {
    return undoState?.length ? new UndoRedoEntry([{ type: 'wasm', entry: undoState }]) : UndoRedoEntry.empty();
  }

  static fromCallback(run: () => UndoRedoEntry) {
    return new UndoRedoEntry([{ type: 'callback', run }]);
  }

  isEmpty() {
    return !this.changes || this.changes.length === 0;
  }

  add(other: UndoRedoEntry) {
    this.changes = [...(this.changes ?? []), ...(other.changes ?? [])];
  }
}

export const MAX_UNDO_STACK_SIZE = 100;

export class UndoRedo {
  private undoStack: UndoRedoEntry[] = [];
  private redoStack: UndoRedoEntry[] = [];

  private undoLimit: number | undefined;
  private redoBeforeBarrier: UndoRedoEntry[] = [];

  constructor() {}

  undo(): UndoRedoEntry | undefined {
    if (this.undoLimit !== undefined && this.undoLimit === this.undoStack.length) return;
    return this.undoStack.pop();
  }

  redo(): UndoRedoEntry | undefined {
    return this.redoStack.pop();
  }

  pushEntry(entry: UndoRedoEntry) {
    if (entry.isEmpty()) {
      return;
    }

    this.undoStack.push(entry);
    this.redoStack = [];
    this.redoBeforeBarrier = [];
    while (this.undoStack.length > MAX_UNDO_STACK_SIZE) {
      this.undoStack.shift();
      if (this.undoLimit !== undefined) {
        this.undoLimit = Math.max(0, this.undoLimit - 1);
      }
    }
  }

  setUndoBarrier() {
    this.undoLimit = this.undoStack.length;
    this.redoBeforeBarrier = this.redoStack;
    this.redoStack = [];
  }

  disableBarrier() {
    if (this.undoLimit === undefined) return;
    const entries = this.undoStack.splice(this.undoLimit);
    if (entries.length > 0) {
      const entry = UndoRedoEntry.empty();
      for (const e of entries) {
        entry.add(e);
      }
      this.pushEntry(entry);
    } else {
      this.redoStack = this.redoBeforeBarrier;
    }
    this.undoLimit = undefined;
    this.redoBeforeBarrier = [];
  }

  pushRedo(entry: UndoRedoEntry) {
    this.redoStack.push(entry);
  }

  pushUndo(entry: UndoRedoEntry) {
    this.undoStack.push(entry);
  }

  clear() {
    this.undoStack = [];
    this.redoStack = [];
    this.redoBeforeBarrier = [];
  }

  hasUndo() {
    return this.undoLimit === undefined ? this.undoStack.length > 0 : this.undoStack.length > this.undoLimit;
  }

  hasRedo() {
    return this.redoStack.length > 0;
  }

  peekTop(): UndoRedoEntry | undefined {
    return this.undoStack[this.undoStack.length - 1];
  }

  /** Removes one recorded step without applying it. The redo stack stays as it is. */
  discard(entry: UndoRedoEntry) {
    const index = this.undoStack.lastIndexOf(entry);
    if (index < 0) {
      return;
    }
    this.undoStack.splice(index, 1);
    if (this.undoLimit !== undefined && index < this.undoLimit) {
      this.undoLimit -= 1;
    }
  }
}
